// ZODA product cards + native cart drawer bridge.
(() => {
  const routeRoot = window.Shopify?.routes?.root || "/";
  const shopifyRoute = (path) => `${routeRoot.replace(/\/?$/, "/")}${path.replace(/^\//, "")}`;
  const controlMessageTimers = new WeakMap();

  const getNativeApp = () => {
    const root = document.documentElement;
    try {
      return window.Alpine?.$data?.(root) || root._x_dataStack?.[0] || null;
    } catch {
      return root._x_dataStack?.[0] || null;
    }
  };

  const openNativeCart = () => {
    const app = getNativeApp();
    if (!app) return false;

    if (typeof app.openCart === "function") {
      app.openCart();
    } else {
      app.cart_drawer = true;
      app.cart_alert = false;
      app.enable_body_scrolling = false;
      app.has_overlay = true;
    }

    return true;
  };

  const setControlBusy = (control, busy) => {
    if (!control) return;
    control.disabled = busy;
    control.setAttribute("aria-busy", busy ? "true" : "false");
    control.classList.toggle("is-busy", busy);
  };

  const getControlLabel = (control) =>
    control?.querySelector("[data-zoda-pdp-add-label]") || control;

  const setControlMessage = (control, message, duration = 1800) => {
    const label = getControlLabel(control);
    if (!control || !label) return;

    const existingTimer = controlMessageTimers.get(control);
    if (existingTimer) window.clearTimeout(existingTimer);

    if (!control.dataset.zodaOriginalText) {
      control.dataset.zodaOriginalText = label.textContent.trim();
    }

    label.textContent = message;

    if (duration > 0) {
      const timer = window.setTimeout(() => {
        label.textContent = control.dataset.zodaOriginalText || label.textContent;
        controlMessageTimers.delete(control);
      }, duration);
      controlMessageTimers.set(control, timer);
    }
  };

  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest("[data-zoda-cart-open]")) {
      event.preventDefault();
      openNativeCart();
    }

    if (target.closest("[data-zoda-cart-close]")) {
      event.preventDefault();
      const app = getNativeApp();
      if (typeof app?.closeCart === "function") app.closeCart();
    }
  });

  const refreshNativeCart = async ({ keepOpen = false } = {}) => {
    const app = getNativeApp();
    if (typeof app?.updateCart === "function") {
      await app.updateCart(false);
      if (keepOpen) {
        openNativeCart();
      }
      return;
    }

    try {
      const cartResponse = await fetch(shopifyRoute("cart.js"), { headers: { Accept: "application/json" } });
      const cart = await cartResponse.json();

      document.querySelectorAll("[data-zoda-cart-count]").forEach((element) => {
        element.textContent = String(cart.item_count);
        element.style.display = cart.item_count > 0 ? "" : "none";
      });
    } catch {}

    if (keepOpen) {
      openNativeCart();
    }
  };

  const addVariantToCart = async (variantId, control) => {
    if (!variantId || control?.dataset.zodaCartBusy === "true") {
      if (!variantId) setControlMessage(control, "Unavailable");
      return;
    }
    const id = Number(variantId);
    if (!Number.isFinite(id)) {
      setControlMessage(control, "Unavailable");
      return;
    }

    if (control) {
      control.dataset.zodaCartBusy = "true";
      setControlBusy(control, true);
    }

    try {
      const response = await fetch(shopifyRoute("cart/add.js"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
      });

      let payload = null;
      try {
        payload = await response.json();
      } catch {}

      if (!response.ok) {
        throw new Error(payload?.description || payload?.message || "Cart add failed");
      }

      setControlMessage(control, "Added", 1200);
      await refreshNativeCart({ keepOpen: true });
    } catch (error) {
      console.error("[ZODA] Add to cart failed:", error);
      const errorMessage = String(error?.message || "");
      setControlMessage(
        control,
        /sold|unavailable|not enough|cannot find|variant/i.test(errorMessage) ? "Unavailable" : "Try again",
        2200,
      );
    } finally {
      if (control) {
        delete control.dataset.zodaCartBusy;
        setControlBusy(control, false);
      }
    }
  };

  const updateProductRailNav = (rail) => {
    const carousel = rail?.closest(".zoda-pim");
    const controls = carousel?.querySelector(".zoda-pim__controls");
    if (!rail || !controls) return;

    const buttons = Array.from(controls.querySelectorAll(".zoda-pim__nav"));
    const isScrollable = rail.scrollWidth > rail.clientWidth + 1;
    controls.style.display = isScrollable ? "" : "none";
    controls.hidden = !isScrollable;
    controls.setAttribute("aria-hidden", isScrollable ? "false" : "true");
    buttons.forEach((button) => {
      button.hidden = !isScrollable;
      button.tabIndex = isScrollable ? 0 : -1;
    });
  };

  const observedProductRails = new WeakSet();
  let productRailNavFrame = 0;
  const railResizeObserver = "ResizeObserver" in window
    ? new ResizeObserver(() => scheduleProductRailNavUpdate())
    : null;
  const railMutationObserver = "MutationObserver" in window
    ? new MutationObserver(() => scheduleProductRailNavUpdate())
    : null;

  const observeProductRail = (rail) => {
    if (observedProductRails.has(rail)) return;
    observedProductRails.add(rail);
    railResizeObserver?.observe(rail);
    railMutationObserver?.observe(rail, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "src", "srcset"],
    });
  };

  const updateAllProductRailNav = () => {
    document.querySelectorAll(".zoda-pim__rail").forEach((rail) => {
      observeProductRail(rail);
      updateProductRailNav(rail);
    });
  };

  function scheduleProductRailNavUpdate() {
    if (productRailNavFrame) return;
    productRailNavFrame = window.requestAnimationFrame(() => {
      productRailNavFrame = 0;
      updateAllProductRailNav();
    });
  }

  scheduleProductRailNavUpdate();
  window.addEventListener("load", scheduleProductRailNavUpdate, { once: true });
  window.addEventListener("resize", scheduleProductRailNavUpdate, { passive: true });
  document.addEventListener("shopify:section:load", scheduleProductRailNavUpdate);

  let sizeDrag = null;
  let suppressSizeClickUntil = 0;

  const endSizeDrag = () => {
    if (!sizeDrag) return;
    if (sizeDrag.row.hasPointerCapture?.(sizeDrag.pointerId)) {
      sizeDrag.row.releasePointerCapture(sizeDrag.pointerId);
    }
    sizeDrag.row.classList.remove("is-dragging");
    sizeDrag = null;
  };

  document.addEventListener(
    "wheel",
    (event) => {
      const target = event.target;
      if (!(target instanceof Element) || event.ctrlKey) return;

      const row = target.closest(".zoda-product-card__sizes-row");
      if (!row || row.scrollWidth <= row.clientWidth + 1) return;

      const primaryDelta =
        Math.abs(event.deltaX) >= Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      if (!primaryDelta) return;

      const maxScroll = row.scrollWidth - row.clientWidth;
      const current = row.scrollLeft;
      const direction = primaryDelta > 0 ? 1 : -1;
      const canScrollForward = direction > 0 && current < maxScroll - 1;
      const canScrollBackward = direction < 0 && current > 1;
      if (!canScrollForward && !canScrollBackward) return;

      event.preventDefault();
      event.stopPropagation();
      row.scrollLeft = Math.max(0, Math.min(maxScroll, current + primaryDelta));
    },
    { passive: false },
  );

  document.addEventListener("pointerdown", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;

    const row = target.closest(".zoda-product-card__sizes-row");
    if (!row || row.scrollWidth <= row.clientWidth + 1) return;

    sizeDrag = {
      row,
      pointerId: event.pointerId,
      pointerType: event.pointerType || "mouse",
      startX: event.clientX,
      startScrollLeft: row.scrollLeft,
      moved: false,
    };
  });

  document.addEventListener(
    "pointermove",
    (event) => {
      if (!sizeDrag || event.pointerId !== sizeDrag.pointerId) return;

      const deltaX = event.clientX - sizeDrag.startX;
      const dragThreshold = sizeDrag.pointerType === "touch" ? 10 : 4;
      if (!sizeDrag.moved && Math.abs(deltaX) > dragThreshold) {
        sizeDrag.moved = true;
        suppressSizeClickUntil = Date.now() + 450;
        sizeDrag.row.classList.add("is-dragging");
        sizeDrag.row.setPointerCapture?.(event.pointerId);
      }

      if (!sizeDrag.moved) return;

      event.preventDefault();
      const maxScroll = sizeDrag.row.scrollWidth - sizeDrag.row.clientWidth;
      sizeDrag.row.scrollLeft = Math.max(
        0,
        Math.min(maxScroll, sizeDrag.startScrollLeft - deltaX),
      );
    },
    { passive: false },
  );

  document.addEventListener(
    "pointerup",
    (event) => {
      if (!sizeDrag || event.pointerId !== sizeDrag.pointerId) return;
      if (sizeDrag.moved) suppressSizeClickUntil = Date.now() + 450;
      endSizeDrag();
    },
    { passive: true },
  );

  document.addEventListener("pointercancel", endSizeDrag, { passive: true });

  document.addEventListener(
    "click",
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (Date.now() > suppressSizeClickUntil) return;
      if (!target.closest(".zoda-product-card__sizes-row")) return;

      event.preventDefault();
      event.stopPropagation();
    },
    true,
  );

  document.addEventListener("click", async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const swatch = target.closest("[data-zoda-card-swatch]");
    if (swatch) {
      event.preventDefault();
      event.stopPropagation();

      const card = swatch.closest(".zoda-product-card");
      if (!card) return;

      card.querySelectorAll("[data-zoda-card-swatch]").forEach((button) => {
        button.classList.remove("is-active");
        button.setAttribute("aria-pressed", "false");
        button.querySelector(".zoda-product-card__swatch-check")?.remove();
      });

      swatch.classList.add("is-active");
      swatch.setAttribute("aria-pressed", "true");
      swatch.insertAdjacentHTML(
        "beforeend",
        '<svg class="zoda-product-card__swatch-check" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 6 9 17l-5-5" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" /></svg>',
      );

      const imageUrl = swatch.getAttribute("data-image-url");
      const image = card.querySelector(".zoda-product-card__media img");
      if (imageUrl && image) {
        image.setAttribute("src", imageUrl);
        image.removeAttribute("srcset");
      }

      const color = swatch.getAttribute("data-color") || "";
      const sizePayload = swatch.getAttribute("data-size-payload") || "";
      card.dataset.zodaSelectedColor = color;
      const sizeMap = new Map(
        sizePayload
          .split("||")
          .map((entry) => entry.trim())
          .filter(Boolean)
          .map((entry) => {
            const [size, id, available] = entry.split("::");
            return [size, { id, available: available === "true" }];
          }),
      );

      card.querySelectorAll(".zoda-product-card__size").forEach((button) => {
        const size = button.textContent.trim();
        const data = sizeMap.get(size);
        const canAdd = Boolean(data?.id) && data.available;

        button.setAttribute("data-variant-id", data?.id || "");
        button.disabled = !canAdd;
        button.classList.toggle("is-unavailable", !canAdd);
        button.setAttribute(
          "aria-label",
          `Add ${card.getAttribute("aria-label") || "product"} - ${color} ${size} to cart`,
        );
      });

      const quickAdd = card.querySelector("[data-zoda-quick-add]");
      if (quickAdd && !card.querySelector("[data-zoda-size-link]")) {
        const swatchVariantId = swatch.getAttribute("data-variant-id") || "";
        const swatchVariantAvailable = swatch.getAttribute("data-variant-available") === "true";
        const canAddSwatchVariant = Boolean(swatchVariantId) && swatchVariantAvailable;

        quickAdd.setAttribute("data-variant-id", swatchVariantId);
        quickAdd.disabled = !canAddSwatchVariant;
        quickAdd.classList.toggle("is-unavailable", !canAddSwatchVariant);
      }

      const selectedSize = card.dataset.zodaSelectedSize;
      if (selectedSize) {
        const selectedSizeButton = Array.from(card.querySelectorAll(".zoda-product-card__size"))
          .find((button) => button.textContent.trim() === selectedSize);

        if (
          selectedSizeButton &&
          !selectedSizeButton.disabled &&
          !selectedSizeButton.classList.contains("is-unavailable")
        ) {
          await addVariantToCart(selectedSizeButton.getAttribute("data-variant-id"), selectedSizeButton);
        }
      }

      return;
    }

    const sizeLink = target.closest("[data-zoda-size-link]");
    if (sizeLink) {
      event.preventDefault();
      event.stopPropagation();

      const variantId = sizeLink.getAttribute("data-variant-id");
      if (!variantId || sizeLink.disabled || sizeLink.classList.contains("is-unavailable")) return;

      const card = sizeLink.closest(".zoda-product-card");
      if (card) {
        card.dataset.zodaSelectedSize = sizeLink.textContent.trim();
      }

      await addVariantToCart(variantId, sizeLink);
      return;
    }

    const button = target.closest("[data-zoda-quick-add]");
    if (!button) return;

    event.preventDefault();

    const id = button.getAttribute("data-variant-id");
    if (!id) return;

    await addVariantToCart(id, button);
  });

})();
